import { ComponentRef, EnvironmentInjector, Inject, Injectable, Injector } from '@angular/core';
import { AuthService } from '@skylab-angular-shared/config';
import { RootContext } from '@skylab-angular-shared/core';
import { AngularFrameworkDelegate, FrameworkDelegate, ThemeSwitcherService, ThemeSwitcherType } from '@skylab-angular-shared/ui';
import { LoggedUserQuery } from '@skylab-shared/business';
import { HttpServiceBase, RootContextBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { makeScripts } from '@skylab-shared/snippets';
import { InsertUserPanelConfigResponse, KontoInsertPanel } from '@skylab-shared/types';

export type UserPanelConfig = Omit<KontoInsertPanel.IUserPanelConfig, 'loadData' | 'onLogin' | 'onLogout'>;
export interface InsertUserPanelServiceConfig<C, P> {
    component: C;
    componentProps: P;
    wrapperId: string;
    texts: KontoInsertPanel.TextsOpts;
}

@Injectable(
    { providedIn: 'root' }
)
export class InsertUserPanelService {
    public constructor(
        private frameworkDelegate: FrameworkDelegate,
        private environmentInjector: EnvironmentInjector,
        private injector: Injector,
        private themeSwitcher: ThemeSwitcherService,
        @Inject(RootContext) private readonly rootContext: RootContextBase,
        private readonly authService: AuthService
    ) { }

    public create<C, P>(config: InsertUserPanelServiceConfig<C, P>): Promise<any> {
        const delegate = this.frameworkDelegate.create(this.environmentInjector, this.injector);
        return this.init(config, delegate);
    }

    private async init<C, P>({ component, componentProps, wrapperId, texts }: InsertUserPanelServiceConfig<C, P>, delegate: AngularFrameworkDelegate): Promise<void> {
        const loadData = await this.loadInsertPanelConfig();
        let element: HTMLElement | null = null;
        let componentRef: ComponentRef<any> | null = null;

        const { companyId: companyIdentityId } = await this.rootContext.getAsync(LoggedUserQuery);
        const innerConfig: KontoInsertPanel.IUserPanelConfig = {
            wrapperId,
            companyIdentityId,
            loadData: () => Promise.resolve(JSON.stringify(loadData.data)),
            onLogout: () => {
                this.authService.logout();
            },
            onLogin: () => {},
            texts,
            accountDisableDefault: true,
            accountOnOpen: async accountPanelGroup => {
                element = accountPanelGroup;
                componentRef = await delegate.attachViewToDom(element, component, componentProps, ['user-panel-list']);
            },
            accountOnClose() {
                delegate.removeViewFromDom(null, componentRef);
            },
            theme: this.themeSwitcher.currentSwitcherType.toUpperCase() as KontoInsertPanel.THEME,
            onThemeChange: theme => {
                this.themeSwitcher.setTheme(theme.toLowerCase() as ThemeSwitcherType);
            },
        };
        await makeScripts(loadData.settings.userPanelUrls.script);
        (window as any).insertUserPanel.init(innerConfig);
    }

    private async loadInsertPanelConfig(): Promise<InsertUserPanelConfigResponse> {
        try {
            const url = TaggedUrl.join('api/konto-insert-panel/header', [Tags.OmitMessage]);
            const result = await HttpServiceBase.get(url);
            return result.data;
        } catch (err) {
            console.error(err);
            throw err;
        }
    }
}
